import { useEffect, useState } from "react";

/**
 * A deliberately tiny client-side router.
 *
 * `003.md` §12 asks for a real information architecture (LIVE / BATTLES /
 * CRICKETS / RULES / REPLAYS). That is five areas, all of which are pure
 * client-side views over the same in-browser engine, so pulling in a routing
 * dependency would add weight without adding capability. This handles exactly
 * the shapes the product needs: literal paths plus one `:id` parameter.
 *
 * If the product later needs nested routes, loaders, or code splitting per
 * route, this is the file to replace with a real router — the rest of the app
 * only depends on `Route` and `navigate`.
 */
export type Route =
  | { readonly name: "live" }
  | { readonly name: "rules" }
  | { readonly name: "crickets" }
  | { readonly name: "create" }
  | { readonly name: "cricket"; readonly id: string }
  | { readonly name: "battles" }
  | { readonly name: "replay"; readonly id: string };

export function parseRoute(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (clean === "/rules") return { name: "rules" };
  if (clean === "/crickets") return { name: "crickets" };
  if (clean === "/crickets/new") return { name: "create" };
  if (clean === "/battles") return { name: "battles" };
  const cricket = /^\/c\/([^/]+)$/.exec(clean);
  if (cricket) return { name: "cricket", id: decodeURIComponent(cricket[1]!) };
  const replay = /^\/replay\/([^/]+)$/.exec(clean);
  if (replay) return { name: "replay", id: decodeURIComponent(replay[1]!) };
  return { name: "live" };
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case "rules":
      return "/rules";
    case "crickets":
      return "/crickets";
    case "create":
      return "/crickets/new";
    case "battles":
      return "/battles";
    case "cricket":
      return `/c/${encodeURIComponent(route.id)}`;
    case "replay":
      return `/replay/${encodeURIComponent(route.id)}`;
    default:
      return "/";
  }
}

/**
 * Pushes a new route.
 *
 * The query string is preserved deliberately: it carries the live match
 * parameters (`?a=…&b=…&seed=…`), so moving between Rules and Live keeps the
 * viewer on the same match. Losing that would make the Rules page a one-way trip.
 */
export function navigate(route: Route, options: { replace?: boolean } = {}): void {
  const url = `${hrefFor(route)}${window.location.search}`;
  if (options.replace) window.history.replaceState(null, "", url);
  else window.history.pushState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);
  return route;
}
